// @flow
import "moment/locale/es";
import * as R from "ramda";
import { DaySlice } from "./HourRange";
import { groupBlocks, mapOfficeHoursToBlocks } from "lib/getRange";
import Icon, { Icons } from "react-hp-icons";
import React, { useState } from "react";
import moment from "moment-timezone";
import styled, { type StyledComponent } from "styled-components";
import theme, {
	type Theme,
	getColor,
	getSize,
	getWeight,
} from "views/resources/theme";
import type { HourBlock, Reserve } from "types";

interface ReserveDateArgs {
	day: number;
	week: number;
	from: moment$Moment;
	to: moment$Moment;
}

interface Props {
	blocks: HourBlock[];
	reserves: Reserve[];
	selectReservationBlock(ReserveDateArgs): void;
}

const DatePicker = ({
	timezone,
	blocks,
	reserves,
	selectReservationBlock,
}: Props) => {
	moment.locale("es");
	moment.tz.setDefault(timezone);
	const today = moment();
	const [state, setState] = useState({
		day: 0,
		week: 0,
		selectedBlock: null,
	});

	const reserve = (from, to, id) => {
		setState((state) => ({
			day: state.day,
			week: state.week,
			selectedBlock: id,
			from,
			to,
		}));

		selectReservationBlock({
			day: state.day,
			week: state.week,
			from,
			to,
		});
	};

	const officeHours = blocks;

	const reservesTZ = reserves.map((r) => ({
		from: moment.utc(r.from),
		to: moment.utc(r.to),
	}));

	const toMoment = (date) =>
		moment(date, "HH:mm:ss.zzz").clone().add(state.day, "d");

	const scheduleSlices = R.pipe(
		// updates office hours to times in the
		// selected date
		R.map(
			R.evolve({
				from: toMoment,
				to: toMoment,
			})
		),
		mapOfficeHoursToBlocks(today.clone(), state.day, reservesTZ),
		groupBlocks,
		R.toPairs,
		R.sortBy(([group, _blocks]) => group)
	)(officeHours);

	const daySlices = scheduleSlices
		.map((slice) => ({
			key: slice[0],
			label: slice[0],
			slices: slice[1],
			selected: state.selectedBlock,
			reserve,
		}))
		.map(DaySlice);

	console.debug("bug01: blocks", blocks);
	console.debug("bug01: scheduleSlices", scheduleSlices);
	console.debug("bug01: daySlices", daySlices);

	// FIXME magic numbers
	const renderDates = (dates) =>
		dates.map((date, idx) => (
			<Date
				number={date.date()}
				selected={idx + state.week * 7 === state.day}
				key={`date-${idx}`}
				onClick={() => {
					const day = idx + state.week * 7;
					setState((state) => ({
						...state,
						week: state.week,
						day,
					}));
					selectReservationBlock({
						week: state.week,
						from: state.from,
						to: state.to,
						day,
					});
				}}
			/>
		));

	const renderWeekDates = (weekdates) =>
		weekdates.map((date, idx) => (
			<div key={`week-${idx}`} style={{ fontSize: "14px" }}>
				{date.locale("es").format("ddd")}
			</div>
		));

	const makeColumn = ([weekday, day], idx) => (
		<Column key={`column-${idx}`}>
			{weekday}
			{day}
		</Column>
	);

	const calendar = R.pipe(
		R.map((i) =>
			today.clone().locale("es").add(i, "day").add(state.week, "week")
		),
		R.juxt([renderWeekDates, renderDates]),
		R.apply(R.zip),
		(x) => x.map(makeColumn)
	)(R.range(0, 7));

	return (
		<Wrapper>
			<Week
				month={today
					.clone()
					.locale(false)
					.add(state.week, "week")
					.locale(false)
					.format("MMMM")}
				year={today.clone().add(state.week, "week").format("YYYY")}
				onLeft={() =>
					setState((state) => ({
						...state,
						week: state.week - 1,
						day: state.day,
					}))
				}
				onRight={() =>
					setState((state) => ({
						...state,
						week: state.week + 1,
						day: state.day,
					}))
				}
			/>
			<Row>{calendar}</Row>
			{!daySlices.length &&
				"No hay bloques horarios disponibles para reservar. Por favor, selecciona otra fecha"}
			{daySlices}
		</Wrapper>
	);
};

DatePicker.defaultProps = {
	blocks: [],
	reserves: [],
};

interface DateProps {
	number: number;
	selected: boolean;
	onClick(): void;
}

const Date = ({ number, selected, onClick }: DateProps) => (
	<SDate
		background={selected ? theme.colors.secondary_light : "transparent"}
		color={selected ? theme.colors.fg_alt : theme.colors.fg}
		onClick={onClick}
	>
		<label>{number}</label>
	</SDate>
);

interface WeekProps {
	month: string;
	year: string;
	onLeft(): void;
	onRight(): void;
}

const Week = ({ month, year, onLeft, onRight }: WeekProps) => (
	<RowDate>
		<Arrow onClick={onLeft}>
			<Icon type={Icons.left} size={1} />
		</Arrow>
		<Dates>
			{month}, {year}
		</Dates>
		<Arrow onClick={onRight}>
			<Icon type={Icons.right} size={1} />
		</Arrow>
	</RowDate>
);

const Wrapper: StyledComponent<*, Theme, *> = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	background-color: ${getColor("bg")};
`;

const Column = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const Row = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 0 ${getSize("m")};
	margin-bottom: 8px;
	align-items: center;
`;

const SDate = styled.div`
	display: inline-block;
	color: ${R.prop("color")};
	background: ${R.prop("background")};
	width: 28px; /*${getSize("l")};*/
	height: 28px;
	margin: 10px;
	line-height: 28px;
	border-radius: 50%;
	text-align: center;
	font-size: 16px;
	cursor: pointer;
	label {
		cursor: pointer;
	}
`;

const RowDate = styled.div`
	margin: 0px 0px 20px;
`;

const Arrow = styled.span`
	display: inline-block;
	margin: 0px 5px;
`;

const Dates = styled.span`
	display: inline-block;
`;

export default DatePicker;
